import styled, { css } from 'styled-components';

import { BreakPoint } from '@makemydeal/dr-activities-common';
import { DOCKED_PANEL_OFFSET_VAR } from '@makemydeal/dr-activities-recent-deals';
import { MAX_APP_CONTAINER_WIDTH_NEW } from '@makemydeal/dr-shared-ui-utils';
import type { HeaderRootProps } from './header.type';

const separatorDot = css`
    border-radius: 50%;
    width: 5px;
    height: 5px;
    background-color: #cad5e2;
`;

export const HeaderRoot = styled.div<HeaderRootProps>`
    z-index: 1001;
    position: fixed;
    top: 0;
    left: var(${DOCKED_PANEL_OFFSET_VAR}, 0);
    right: 0;
    background-color: #ffffff;
    border-bottom: ${({ showBorder, theme }) => (!showBorder ? `4px solid ${theme.tokens?.BaseColorBlue800}` : '')};
`;

export const HeaderContainer = styled.div`
    background-color: #ffffff;
`;

export const HeaderPanelsRow = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @media (min-width: ${BreakPoint.SMALL}) {
        padding: 0 1rem;
        flex-direction: row;
    }
`;

export const HeaderRowContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-width: ${MAX_APP_CONTAINER_WIDTH_NEW};
    margin: 0 auto;
    padding: 0.6rem 1rem 0.7rem 1rem;
    padding-left: 1.14rem;
    border-bottom: ${({ theme }) => `0.3rem solid ${theme.tokens?.BaseColorBlue700}`};
    border-top: ${({ theme }) => `1px solid ${theme.tokens?.BaseColorGray300}`};
    &.compact-ui-header {
        padding-bottom: 0;
    }
`;

export const DealInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: end;
`;

export const ModalWrapperContainer = styled.div`
    position: relative;
`;

export const HeaderContentRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    justify-content: space-between;
    color: #000;
    width: 100%;

    @media (max-width: 480px) {
        flex: 1;
    }
`;

export const HeaderShopperNameAndActions = styled.div`
    display: flex;
    flex-direction: row;
    gap: inherit;
    &.compact-ui-header-shopper-name-and-actions {
        gap: 0.6rem;
    }

    @media (max-width: ${BreakPoint.PHONE}) {
        flex-direction: column;
    }
`;

export const HeaderActionsSeparator = styled.span<{
    $isThick?: boolean;
}>`
    margin: ${({ $isThick }) => ($isThick ? '0' : '6px 0')};
    border-left: ${({ $isThick }) => ($isThick ? '2px' : '1px')} solid;
    color: #959595;
    &.compact-ui-header-actions-separator {
        color: #cad5e2;
    }

    @media (max-width: ${BreakPoint.PHONE}) {
        &:first-of-type {
            display: none;
        }
    }
`;

export const HeaderActionsContainer = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    &.compact-ui-header-actions-container {
        gap: 0.6rem;
    }
`;

export const BackButtonRow = styled.div`
    max-width: ${MAX_APP_CONTAINER_WIDTH_NEW};
    margin-bottom: 0.625rem;
    margin: 0 auto;
    padding: 0 0.5rem;
    padding-top: '0.6rem';
`;

export const HeaderContent = styled.div`
    background-color: ${({ theme }) => theme.tokens?.BaseColorWhite};

    @media (min-width: ${BreakPoint.PHONE}) {
        padding-top: 0;
    }
`;

export const HeaderBuyerContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.6rem 1rem;
    flex-grow: 1;
    width: 100%;

    @media (max-width: ${BreakPoint.PHONE}) {
        flex-direction: column;
    }
`;

export const ModifiedIndicatorContainer = styled.div`
    position: absolute;
    z-index: 100000;
    top: 0;
    right: 0.3rem;
    font-size: 2rem;
    color: red;
`;

export const BridgeBarContainer = styled.div`
    flex: 1;
    padding-top: 3px;
`;

export const TopBarContainer = styled.div`
    background-color: #ffffff;
    display: flex;
    padding: 0px;
    align-items: center;
    gap: 0.5rem;
    max-width: ${MAX_APP_CONTAINER_WIDTH_NEW};
    margin: 0 auto;
    width: 100%;
`;

export const HamburgerMenuContainer = styled.div`
    width: 40px;
    flex-shrink: 0;
`;

export const HeaderEnhancedShopperDetails = styled.div`
    // mobile rules first
    display: grid;
    grid-template-columns: 1fr 1fr; /* Two equal-width columns */
    grid-template-rows: auto auto auto; /* Three rows, auto-sized */
    row-gap: 0.75rem;

    // // Tablet and above
    @media (min-width: ${BreakPoint.SMALL}) {
        display: flex;
        flex-direction: row;
        gap: 0.5rem 2.5rem;
        flex-wrap: nowrap;
    }
`;

export const ShopperDetailItem = styled.div<{ gridSpan?: number; fieldKey?: string; hasSeparator?: boolean; isLastItem?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    row-gap: 0.25rem;
    align-self: flex-start;
    grid-column: span ${({ gridSpan = 1 }) => gridSpan};
    position: relative;
    &::after {
        content: '';
        display: ${({ hasSeparator, isLastItem }) => (hasSeparator && !isLastItem ? 'block' : 'none')};
        position: absolute;
        top: 6px;
        right: -20px;
        ${separatorDot}
        @media (max-width: ${BreakPoint.SMALL}) {
            display: none;
        }
    }
`;

export const CobuyerActionsSeparator = styled.span`
    margin: 5px 0;
    border-left: 1px solid;
    color: #959595;
    &.compact-ui-cobuyer-actions-separator {
        color: #cad5e2;
    }
`;

export const CobuyerActionDot = styled.span`
    ${separatorDot}
    flex: 0 0 auto;
`;

export const HeaderModeSwitchArea = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

export const HeaderDealContainer = styled.div`
    display: flex;
    gap: inherit;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
`;

export const HeaderDetailsAndAssistantRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
`;

export const HeaderAssistantSlot = styled.div`
    flex: 0 1 360px;
    min-width: 0;
    margin-left: auto;
`;
